import { actionCreatorFactory, Action, AsyncActionCreators, Success, Failure } from 'typescript-fsa'
import { AppMetaData, CollectionItems, Collection, Screen, DevicePlatform, MediaItem } from './types'
import {
	AppToken,
	Manifest,
	GetMediaItemsShowResponse,
	RedeemCodeResponse,
	RedeemCode,
	MapRoute,
	Form,
	BundleFull,
	BundleItem,
	GetInAppPurchasesIndexResponse,
} from 'mytours-read-api-typescript'

const actionCreator = actionCreatorFactory('Api')

// loads meta data about an app from network
export interface RequestParams {
	projectId?: string // Unused
	devicePlatform: DevicePlatform
	deviceUdid?: string // Unused
	onError?: (error: Error | Response) => void
}
export type LoadAppMetaDataFromNetworkParams = Action<RequestParams>
type LoadAppMetaDataFromNetworkSuccess = Success<RequestParams, AppMetaData>
export type LoadAppMetaDataFromNetworkResult = Action<LoadAppMetaDataFromNetworkSuccess>
type LoadAppMetaDataFromNetworkFailure = Failure<RequestParams, Error | Response>
export type LoadAppMetaDataFromNetworkResultFailure = Action<LoadAppMetaDataFromNetworkFailure>
export const fetchAppMetaData = actionCreator.async<RequestParams, AppMetaData, Error | Response>(
	'FETCH_APP_META_DATA_FROM_NETWORK',
)

// loads a collection with items from network
export interface CollectionItemsRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	collectionId: number
	collectionVersion: number
	collectionSubtype: 'list' | 'tour' | 'menu' // parent collection subtype - so we know where to store results on redux
	accountId?: number
	language: string
	page?: number
	perPage?: number
	full?: boolean // true to fetch the whole collection in one request, each item carrying its screen
	isPreCaching: boolean // true if downloading item as part of the sync content feature
}
export type LoadCollectionItemsFromNetworkParams = Action<CollectionItemsRequestParams>
type LoadCollectionItemsFromNetworkSuccess = Success<CollectionItemsRequestParams, CollectionItems>
export type LoadCollectionItemsFromNetworkResult = Action<LoadCollectionItemsFromNetworkSuccess>
type LoadCollectionItemsFromNetworkFailure = Failure<CollectionItemsRequestParams, Error>
export type LoadCollectionItemsFromNetworkResultFailure = Action<LoadCollectionItemsFromNetworkFailure>
export const fetchCollectionItems = actionCreator.async<CollectionItemsRequestParams, CollectionItems, Error>(
	'FETCH_COLLECTION_ITEMS_FROM_NETWORK',
)

// loads a collection from network
export interface CollectionRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	collectionId: number
	collectionVersion: number
	accountId?: number
	language: string
	isPreCaching: boolean // true if downloading item as part of the sync content feature
}
export type LoadCollectionFromNetworkParams = Action<CollectionRequestParams>
type LoadCollectionFromNetworkSuccess = Success<CollectionRequestParams, Collection>
export type LoadCollectionFromNetworkResult = Action<LoadCollectionFromNetworkSuccess>
export const fetchCollection = actionCreator.async<CollectionRequestParams, Collection, Error>(
	'FETCH_COLLECTION_FROM_NETWORK',
)

export interface BundleRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid?: string
	bundleId: number
	bundleVersion: number
	/** The bundle's own account, not the app's — a guide client's bundle is packaged under the client. */
	accountId?: number
	language: string
}
export type LoadBundleFromNetworkParams = Action<BundleRequestParams>
export const fetchBundle = actionCreator.async<BundleRequestParams, BundleFull, Error>('FETCH_BUNDLE_FROM_NETWORK')

// loads a screen from network
export interface ScreenRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	screenId: number
	screenVersion: number
	accountId?: number
	language: string
	isPreCaching: boolean // true if downloading item as part of the sync content feature
}
export type LoadScreenFromNetworkParams = Action<ScreenRequestParams>
type LoadScreenFromNetworkSuccess = Success<ScreenRequestParams, Screen>
export type LoadScreenFromNetworkResult = Action<LoadScreenFromNetworkSuccess>
type LoadScreenFromNetworkFailure = Failure<ScreenRequestParams, Error>
export type LoadScreenFromNetworkResultFailure = Action<LoadScreenFromNetworkFailure>
export const fetchScreen = actionCreator.async<ScreenRequestParams, Screen, Error>('FETCH_SCREEN_FROM_NETWORK')

export interface RentalTokenParams {
	appId: number
	token: string
}
export type ValidateRentalTokenParams = Action<RentalTokenParams>
export const validateRentalToken = actionCreator.async<RentalTokenParams, AppToken, Error>('VALIDATE_RENTAL_TOKEN')

export interface ManifestRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	isForSync: boolean // true if downloading manifest for syncing all content to device
	keypadNumber?: number // is set when we need to load screen details from the keypad entry
}
export type LoadManifestFromNetworkParams = Action<ManifestRequestParams>
type LoadManifestFromNetworkSuccess = Success<ManifestRequestParams, Manifest>
export type LoadManifestFromNetworkResult = Action<LoadManifestFromNetworkSuccess>
export const fetchManifest = actionCreator.async<ManifestRequestParams, Manifest, Error>('FETCH_MANIFEST_FROM_NETWORK')

// manifest was successfully loaded from disk to be put onto redux store
export const fetchManifestComplete = actionCreator<Manifest>('LOADED_MANIFEST_COMPLETE')

// loads a media item from network
export interface MediaItemRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	itemId: number
	itemVersion: number
	language: string
	isPreCaching: boolean
}
export type LoadMediaItemFromNetworkParams = Action<MediaItemRequestParams>
type LoadMediaItemFromNetworkSuccess = Success<MediaItemRequestParams, MediaItem>
export type LoadMediaItemFromNetworkResult = Action<LoadMediaItemFromNetworkSuccess>
type LoadMediaItemFromNetworkFailure = Failure<MediaItemRequestParams, Error>
export type LoadMediaItemFromNetworkResultFailure = Action<LoadMediaItemFromNetworkFailure>
export const fetchMediaItem = actionCreator.async<MediaItemRequestParams, GetMediaItemsShowResponse, Error>(
	'FETCH_MEDIA_ITEM_FROM_NETWORK',
)

// loads a form from network
export interface FormRequestParams {
	id: number
	version: number
	language: string
	isPreCaching: boolean
}
export type LoadFormFromNetworkParams = Action<FormRequestParams>
type LoadFormFromNetworkSuccess = Success<FormRequestParams, Form>
export type LoadFormFromNetworkResult = Action<LoadFormFromNetworkSuccess>
type LoadFormFromNetworkFailure = Failure<FormRequestParams, Error>
export type LoadFormFromNetworkResultFailure = Action<LoadFormFromNetworkFailure>
export const fetchForm = actionCreator.async<FormRequestParams, Form, Error>('FETCH_FORM_FROM_NETWORK')

// loads map route from network
export interface MapRouteRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	linkedId: number
	linkedType: MapRoute.LinkedTypeEnum
	mapRouteId: number
	mapRouteVersion: number
	language: string
	accountId?: number
}
export type LoadMapRouteFromNetworkParams = Action<MapRouteRequestParams>
type LoadMapRouteFromNetworkSuccess = Success<MapRouteRequestParams, MapRoute>
export type LoadMapRouteFromNetworkResult = Action<LoadMapRouteFromNetworkSuccess>
type LoadMapRouteFromNetworkFailure = Failure<MapRouteRequestParams, Error>
export type LoadMapRouteFromNetworkResultFailure = Action<LoadMapRouteFromNetworkFailure>
export const fetchMapRoute = actionCreator.async<MapRouteRequestParams, MapRoute, Error>('FETCH_MAP_ROUTE_FROM_NETWORK')

export interface CouponCodeRequestParams {
	linkedId: number
	linkedType: RedeemCode.LinkedTypeEnum
	devicePlatform: DevicePlatform
	deviceUdid: string
	couponCode: string
	email?: string
}
export type LoadCouponCodeParams = Action<CouponCodeRequestParams>

type LoadCouponCodeSuccess = Success<CouponCodeRequestParams, RedeemCodeResponse>
export type LoadCouponCodeResultSuccess = Action<LoadCouponCodeSuccess>

export interface ValidateCodeParams {
	linkedId: number
	linkedType: RedeemCode.LinkedTypeEnum
	devicePlatform: DevicePlatform
	deviceUdid: string
	couponCode: string
	expireAt?: number
	onComplete?: (isValid: boolean) => void
}
export type ValidateCodeAction = Action<ValidateCodeParams>

type ValidateCodeSuccess = Success<ValidateCodeParams, RedeemCodeResponse>
export type ValidateCodeResultSuccess = Action<ValidateCodeSuccess>
type ValidateCodeFailure = Failure<ValidateCodeParams, Error>
export type ValidateCodeResultFailure = Action<ValidateCodeFailure>

export const validateCode = actionCreator.async<ValidateCodeParams, RedeemCodeResponse, Error>(
	'COUPON_VALIDATE_APP_CODE',
)
export const redeemCode = actionCreator.async<CouponCodeRequestParams, RedeemCodeResponse, Error>(
	'COUPON_REDEEM_APP_CODE',
)

// ---- Bundles (Phase 1: text-code redemption) ----

// Error-message constants the bundles reducer uses to translate RedeemCodeResponse
// statuses into an Error. The bundle dialog maps these back to localized strings.
export const ERROR_MSG_INVALID_COUPON = 'INVALID'
export const ERROR_MSG_EXPIRED_COUPON = 'EXPIRED'
export const ERROR_MSG_MAX_REDEMPTIONS_COUPON = 'MAX_REDEMPTIONS'
export const ERROR_MSG_REQUIRES_EMAIL_COUPON = 'REQUIRES_EMAIL'

export interface RedeemBundleCouponPayload {
	devicePlatform: DevicePlatform
	coupon: string
	email?: string
	id: number
}
export interface RedeemBundleCouponResult extends RedeemCodeResponse {
	items?: BundleItem[]
}
export const redeemBundleCoupon = actionCreator.async<RedeemBundleCouponPayload, RedeemBundleCouponResult, Error>(
	'REDEEM_BUNDLE_COUPON',
)

// Shared payload for re-validating an already-redeemed code rebuilt from disk (bundles and
// owned collections use the same flow, differing only in linked type / target slice).
export interface ValidateRedeemedCodePayload {
	devicePlatform: DevicePlatform
	coupon: string
	id: number
	expireAt?: number
	addedAt?: number
}
export type ValidateRedeemedCodeResultSuccess = Action<Success<ValidateRedeemedCodePayload, RedeemCodeResponse>>
export type ValidateRedeemedCodeResultFailure = Action<Failure<ValidateRedeemedCodePayload, Error>>
// The contract the shared revalidation sagas depend on — validateBundleCoupon and
// validateOwnedCollection are interchangeable behind it.
export type ValidateRedeemedCodeActionCreator = AsyncActionCreators<
	ValidateRedeemedCodePayload,
	RedeemCodeResponse,
	Error
>

export const validateBundleCoupon = actionCreator.async<ValidateRedeemedCodePayload, RedeemCodeResponse, Error>(
	'VALIDATE_BUNDLE_COUPON',
)

// Revalidates redeemed COLLECTION codes for the Purchased Content slice — a dedicated action (not
// the shared validateCode) so it never touches lockapp's per-collection lock UI.
export const validateOwnedCollection = actionCreator.async<ValidateRedeemedCodePayload, RedeemCodeResponse, Error>(
	'VALIDATE_OWNED_COLLECTION',
)

// Fetches the IAP catalogue; used to resolve a bundle's items list and to reconcile
// already-redeemed bundles when the builder edits them.
export interface InAppPurchasesRequestParams {
	devicePlatform: DevicePlatform
	version: number
	language: string
}
export const fetchInAppPurchases = actionCreator.async<
	InAppPurchasesRequestParams,
	GetInAppPurchasesIndexResponse,
	Error
>('FETCH_IN_APP_PURCHASES')

// Action-type aliases used by the bundle sagas.
export type RedeemBundleCouponParams = Action<RedeemBundleCouponPayload>
export type RedeemBundleCouponResultSuccess = Action<Success<RedeemBundleCouponPayload, RedeemBundleCouponResult>>
export type FetchInAppPurchasesParams = Action<InAppPurchasesRequestParams>
